import { useCallback, useEffect, useState } from 'react';

import noop from '../noop';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ChangeHandler<T> = (v: T, ...args: any[]) => void;

/**
 * Expose an interface to implement a controlled component
 * @param defaultValue Default value for the input using the hook
 * @param value Current value
 * @param onChange Callback to be fired on input change
 * @returns a tuple containing the final value and a callback for change event which runs `onChange`
 * @example
 * const ControlledInput = React.forwardRef(
 *   (
 *     {
 *       prop,
 *       defaultProp = false,
 *       onChange,
 *       ...props
 *     },
 *     ref
 *   ) => {
 *     const [value, handleOnChange] = useControlled(
 *       defaultProp,
 *       prop,
 *       onChange
 *     );
 *
 *     return (
 *       <input
 *         ref={ref}
 *         name={name}
 *         value={value}
 *         onChange={onChange}
 *       />
 *     );
 *   }
 * )
 * @see Button Checkbox CommentField Menu RadioButton Slider Table Tag Tile Toggle
 */
export default function useControlled<T>(
  defaultValue: T,
  value?: T,
  onChange: ChangeHandler<T> = noop,
): [T, ChangeHandler<T>] {
  const [innerValue, setInnerValue] = useState<T>(defaultValue);

  // Sync internal state with controlled value when `value` changes
  useEffect(() => {
    if (value !== undefined) {
      setInnerValue(value);
    }
  }, [value]);

  const handleChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (v: T, ...args: any[]) => {
      if (value === undefined) {
        setInnerValue(v);
      }
      onChange(v, ...args);
    },
    [onChange, value],
  );

  return [value !== undefined ? value : innerValue, handleChange];
}
